import { datadogLogs } from '@datadog/browser-logs';
import { QUERY_SESSION_NOTE_KEY } from '@web-app/hooks/consts';
import { Note } from '@web-app/server/api/types';
import { molecule } from 'bunshi';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { SessionScope } from '../scopes';

/**
 * Session Note Molecule
 *
 * Provides all session note atoms scoped to a session (keyed by roomId).
 * This molecule is automatically created and destroyed when entering/leaving a session.
 *
 * The atoms within this molecule:
 * - `sessionNoteQueryAtom`: Fetches session note data by roomId
 * - `sessionNoteAtom`: Derived from query result with local optimistic updates
 * - `sessionNoteLoadingAtom`: Indicates if the query is loading
 * - `sessionNoteErrorAtom`: Contains any query error
 * - `sessionNoteFetchingAtom`: Indicates if the query is fetching
 * - `refetchSessionNoteAtom`: Action atom to manually refetch
 * - `resetSessionNoteAtom`: Action atom to reset local state
 */
export const sessionNoteMolecule = molecule((_, scope) => {
  const roomId = scope(SessionScope);

  // ============================================================================
  // HELPER FUNCTION
  // ============================================================================

  /**
   * Helper function to fetch session note from API.
   *
   * Fetches the note data from /api/session-notes/{id}/note.
   * Handles 404 by returning a Note object with sessionNote: null.
   * Logs errors to Datadog for monitoring.
   */
  const getSessionNote = async (id?: string): Promise<Note | undefined> => {
    if (!id) return undefined;
    const res = await fetch(`/api/session-notes/${id}/note`);
    if (res.status === 404) {
      const emptyNote: Note = { sessionNote: null };
      return emptyNote;
    }
    if (!res.ok) {
      const error = new Error(
        `Failed to fetch session note: ${res.status} ${res.statusText}`,
      );
      datadogLogs.logger.error('Failed to fetch session note', {
        feature: 'video-call',
        workflow: 'session-note',
        sessionId: id,
        status: res.status,
        statusText: res.statusText,
      });
      throw error;
    }
    try {
      const data = await res.json();
      return data;
    } catch (jsonError) {
      datadogLogs.logger.error(
        'Failed to parse JSON response for session note',
        {
          feature: 'video-call',
          workflow: 'session-note',
          sessionId: id,
          originalStatus: res.status,
        },
        jsonError instanceof Error ? jsonError : undefined,
      );
      throw new Error(`Failed to parse JSON response for session note: ${id}`);
    }
  };

  // ============================================================================
  // SESSION NOTE QUERY ATOM
  // ============================================================================

  /**
   * Query atom that automatically fetches session note data when roomId changes.
   */
  const sessionNoteQueryAtom = atomWithQuery(() => ({
    queryKey: [QUERY_SESSION_NOTE_KEY, roomId],
    queryFn: async () => getSessionNote(roomId),
    enabled: !!roomId,
  }));

  // ============================================================================
  // SESSION NOTE LOCAL STATE
  // ============================================================================

  /**
   * Local state for optimistic updates.
   *
   * When the user modifies the session note, we update this local state
   * immediately while the query is still loading/syncing. This provides
   * a responsive UI while the server processes the change.
   */
  const sessionNoteLocalAtom = atom<Note | undefined>(undefined);

  // ============================================================================
  // SESSION NOTE ATOMS
  // ============================================================================

  /**
   * Provides access to the current session note, with local optimistic updates.
   *
   * Read:
   * - Returns the local value if it's been set (optimistic update)
   * - Otherwise returns the query data
   *
   * Write:
   * - Updates the local state (supports function form for derived updates)
   */
  const sessionNoteAtom = atom(
    (get) => {
      const localValue = get(sessionNoteLocalAtom);
      if (localValue !== undefined) return localValue;
      const queryResult = get(sessionNoteQueryAtom);
      return queryResult.data;
    },
    (
      get,
      set,
      newValue:
        | Note
        | undefined
        | ((prev: Note | undefined) => Note | undefined),
    ) => {
      if (typeof newValue === 'function') {
        const currentValue = get(sessionNoteAtom);
        set(sessionNoteLocalAtom, newValue(currentValue));
      } else {
        set(sessionNoteLocalAtom, newValue);
      }
    },
  );

  /**
   * Indicates if the session note query is loading.
   */
  const sessionNoteLoadingAtom = atom((get) => {
    const queryResult = get(sessionNoteQueryAtom);
    return queryResult.isLoading;
  });

  /**
   * Contains any error that occurred while fetching the session note.
   */
  const sessionNoteErrorAtom = atom((get) => {
    const queryResult = get(sessionNoteQueryAtom);
    return queryResult.error;
  });

  /**
   * Indicates if the session note query is currently fetching.
   */
  const sessionNoteFetchingAtom = atom((get) => {
    const queryResult = get(sessionNoteQueryAtom);
    return queryResult.isFetching;
  });

  /**
   * Action atom to manually refetch the session note.
   */
  const refetchSessionNoteAtom = atom(null, async (get) => {
    const queryResult = get(sessionNoteQueryAtom);
    await queryResult.refetch();
  });

  /**
   * Action atom to reset the local state.
   *
   * This clears any optimistic updates, allowing the query data to show through.
   */
  const resetSessionNoteAtom = atom(null, (_get, set) => {
    set(sessionNoteLocalAtom, undefined);
  });

  return {
    sessionNoteQueryAtom,
    sessionNoteAtom,
    sessionNoteLoadingAtom,
    sessionNoteErrorAtom,
    sessionNoteFetchingAtom,
    refetchSessionNoteAtom,
    resetSessionNoteAtom,
  };
});
